import { useEffect, useRef, useState } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { Button } from "./Button";
import { Field } from "./Field";
import { Input } from "./Input";
import { useToast } from "./Toast";
import { cn } from "@/lib/utils/cn";

/**
 * Asset picker: paste a URL, or upload a file.
 *
 * Uploads store the returned `signed_id` rather than the URL — a signed ID
 * survives a storage backend change, a baked-in URL does not.
 */
export function UploadField({
  label, value, onChange, onUpload, onContentTypeChange, hint, accept = "image/*", preview = true, previewSrc,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  onUpload?: (asset: { signed_id: string; content_type: string; filename: string }) => void;
  onContentTypeChange?: (contentType: string | undefined) => void;
  hint?: string;
  accept?: string;
  preview?: boolean;
  previewSrc?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const toast = useToast();

  const isAudio = accept.includes("audio");
  const imageSrc = value ? resolveAssetUrl(value) : previewSrc;

  useEffect(() => setPreviewFailed(false), [imageSrc]);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const asset = await adminApi.uploadAsset(file);
      if (onUpload) {
        onUpload(asset);
      } else {
        onChange(asset.signed_id);
        onContentTypeChange?.(asset.content_type);
      }
      toast.success(`Uploaded ${asset.filename}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-2">
        {preview && !isAudio && (
          <div
            className={cn(
              "grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md border border-ink-600 bg-ink-800",
            )}
          >
            {imageSrc && !previewFailed ? (
              <img src={imageSrc} alt="" onError={() => setPreviewFailed(true)} className="h-full w-full object-cover" />
            ) : previewFailed ? (
              <span className="px-1 text-center text-[9px] leading-tight text-amber-300">Preview unavailable. Use PNG.</span>
            ) : (
              <ImageGlyph />
            )}
          </div>
        )}
        <Input
          value={value ?? ""}
          placeholder="Paste URL or upload"
          spellCheck={false}
          onChange={(event) => { onChange(event.target.value || undefined); onContentTypeChange?.(undefined); }}
          className="font-mono text-xs"
        />
        <Button size="md" loading={uploading} onClick={() => inputRef.current?.click()} icon={<UploadGlyph />}>
          Upload
        </Button>
        {value && (
          <Button size="md" variant="ghost" onClick={() => { onChange(undefined); onContentTypeChange?.(undefined); }} aria-label={`Clear ${label}`}>
            ✕
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />
    </Field>
  );
}

/**
 * Turns whatever the config holds into something an <img> can load.
 *
 * Config values are either an Active Storage signed ID, an absolute URL, or a
 * legacy Rails blob path — all three are still in the wild.
 */
export function resolveAssetUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("data:")) return value;
  if (value.startsWith("/")) {
    const publicBackend = import.meta.env.VITE_BACKEND_URL?.replace(/\/$/, "");
    // Send Active Storage directly to Rails. This prevents Rails' redirect
    // from being generated from the frontend tunnel's host.
    if (publicBackend && value.startsWith("/rails/active_storage/")) {
      return `${publicBackend}${value}`;
    }
    return value;
  }
  // Rails may serialize Active Storage URLs with the local API host. On a
  // tunnel that host belongs to the viewer's machine, so make it same-origin.
  const localBackendUrl = value.replace(/^https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0)(?::\d+)?/i, "");
  if (localBackendUrl !== value && localBackendUrl.startsWith("/")) {
    const publicBackend = import.meta.env.VITE_BACKEND_URL?.replace(/\/$/, "");
    return publicBackend ? `${publicBackend}${localBackendUrl}` : localBackendUrl;
  }
  if (value.startsWith("http")) {
    try {
      const url = new URL(value);
      // Backend URLs may contain localhost:3001 during local development.
      // Keep the path relative so a remote tunnel does not send the guest's
      // browser to its own localhost; Vite proxies this path to Rails.
      if (["localhost", "127.0.0.1", "0.0.0.0"].includes(url.hostname)) {
        return `${url.pathname}${url.search}`;
      }
    } catch {
      // Fall through and let the browser handle an unusual but valid-looking URL.
    }
    return value;
  }
  // Proxy through Rails so Garage's internal Docker hostname never reaches
  // the browser. A redirect route would expose http://garage:3900/... to the
  // public frontend and fail outside the Compose network.
  const activeStoragePath = `/rails/active_storage/blobs/proxy/${value}/asset`;
  const publicBackend = import.meta.env.VITE_BACKEND_URL?.replace(/\/$/, "");
  return publicBackend ? `${publicBackend}${activeStoragePath}` : activeStoragePath;
}

function ImageGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-haze-400">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

function UploadGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 16V4M12 4l-4 4M12 4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
